import React, { useContext, useEffect, useMemo } from 'react';
import MultipleSelect from './MultipleSelect';
import SelectedItem from 'components/SelectedItem/SelectedItem';
import { AppContext, IAppContext } from 'appState/appContext';
import {
	CustomerFullInfoContext,
	ICustomerFullInfoContext,
} from 'modules/CustomersAndRestaurants/Customers/CustomerFullInfo/customerFullInfoState/customerFullInfoContext';
import { IPushesViewContext, PushesViewContext } from '../../../../pushesViewContext/pushesViewState/pushesViewContext';
import { UserRoles, IGetRestaurantsShort, IGetRestaurantsShortArgs } from 'types';
import { getRestaurantsShortInfo } from 'backend/restaurants';
import { useFetchData } from 'hooks/useFetchData';
import { useRoleAccess } from 'hooks/useRoleAccess';
import style from './RestaurantMultipleSelect.module.scss';

interface IRestaurantMultipleSelect {
	isVisibleAdditionalModal: boolean;
}

const RestaurantMultipleSelect: React.FC<IRestaurantMultipleSelect> = ({ isVisibleAdditionalModal }) => {
	const { loggedUser } = useContext<IAppContext>(AppContext);
	const { customerForFullInfo } = useContext<ICustomerFullInfoContext>(CustomerFullInfoContext);
	const { setFetchedRestaurants, setRestaurantsForPush, fetchedRestaurants, restaurantsForPush } =
		useContext<IPushesViewContext>(PushesViewContext);
	const hasAccess = useRoleAccess([
		UserRoles.SUPER_USER,
		UserRoles.CLIENT_MANAGER,
		UserRoles.GROUP_MANAGER,
		UserRoles.TECHNICAL,
	]);

	const { response, loading, refetch, responseError } = useFetchData<IGetRestaurantsShort, IGetRestaurantsShortArgs>(
		customerForFullInfo?.id && !fetchedRestaurants.length ? getRestaurantsShortInfo : null,
		{
			customerId: customerForFullInfo?.id || '',
		},
	);

	const restaurants = useMemo(() => {
		const accessibleRestaurantIDs = loggedUser.restaurants ? loggedUser.restaurants.map((el) => el.id) : [];
		const intersectedRestaurants = fetchedRestaurants.filter((el) => accessibleRestaurantIDs.includes(el.id));
		return hasAccess ? fetchedRestaurants : intersectedRestaurants;
	}, [fetchedRestaurants, loggedUser, hasAccess]);

	const handleRemove = (id: string) => {
		const selectedItems = restaurantsForPush.filter((el) => el !== id);
		setRestaurantsForPush(selectedItems);
	};

	const restaurantsList = useMemo(() => {
		return restaurantsForPush.map((el) => {
			const label = fetchedRestaurants?.find((item) => item.id === el)?.name || '';
			return <SelectedItem key={el} label={label} onDelete={() => handleRemove(el)} />;
		});
	}, [handleRemove, restaurantsForPush, restaurants]);

	useEffect(() => {
		if (response) {
			setFetchedRestaurants(response.data.restaurants);
		}
	}, [response]);

	return (
		<div className={style.container} data-test="RestaurantField">
			<div className={style.field}>
				<MultipleSelect
					data={restaurants || []}
					isLoading={loading}
					refetch={refetch}
					responseError={responseError}
					setRestaurantsForPush={setRestaurantsForPush}
					selectedAdditionalRestaurants={restaurantsForPush}
					isVisibleAdditionalModal={isVisibleAdditionalModal}
				/>
			</div>
			<div className={style.groupsList}>{restaurantsList}</div>
		</div>
	);
};

export default RestaurantMultipleSelect;
